import { useMemo } from 'react';

import { IFilterDefinition, ISelectFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';

import { ENVIRONMENT_LABEL_KEY } from '../../../common/consts/platformConfigConsts';
import { LabelsExcludedFromDashboard } from '../../../common/types/ConfigurationTypes';
import { DASHBOARD_DATE_RANGE_OPTIONS } from '../constants/DashboardFiltersDefinitions';
import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from '../helpers/ConfigurationProcessingHelpers';
import { sanitizeConfigurationLabel } from '../helpers/DataSanitizationHelpers';
import { createSingleSelectEnvironmentFilter } from '../helpers/FilterCreationHelpers';

const DATE_RANGE_FILTER: ISelectFilterDefinition = {
	id: 'dateRange',
	label: 'Date range',
	filterType: 'select',
	defaultValue: [DASHBOARD_DATE_RANGE_OPTIONS[0]],
	componentProps: {
		placeholder: 'Last 7 days',
		isMultiple: false,
		id: 'dateRange',
		label: 'Date range',
		listboxWidth: '180px'
	}
};

export const useConfigurationFilters = (): {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
	labelsExcludedFromDashboard: LabelsExcludedFromDashboard;
} => {
	const { configuration } = useConfiguration();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } = useMemo(() => {
		if (!configuration?.PLATFORM) {
			return {
				filterDefinitions: [DATE_RANGE_FILTER],
				filterOptions: { dateRange: DASHBOARD_DATE_RANGE_OPTIONS },
				labelsExcludedFromDashboard: {}
			};
		}

		const { coreLabels, isValid } = processPlatformConfiguration(configuration.PLATFORM);

		if (!isValid) {
			return {
				filterDefinitions: [DATE_RANGE_FILTER],
				filterOptions: { dateRange: DASHBOARD_DATE_RANGE_OPTIONS },
				labelsExcludedFromDashboard: {}
			};
		}

		const environmentLabel = coreLabels.find((label) => label.label === ENVIRONMENT_LABEL_KEY);

		const allFilters: ISelectFilterDefinition[] = [];
		const allOptions: Record<string, OptionType[]> = {
			dateRange: DASHBOARD_DATE_RANGE_OPTIONS
		};

		if (environmentLabel) {
			const sanitized = sanitizeConfigurationLabel(environmentLabel);

			if (sanitized.values.length > 0) {
				const environmentOptions: OptionType[] = sanitized.values.map((value) => ({
					value: String(value),
					label: String(value)
				}));

				// Set default value to first environment
				const defaultEnvironmentValue: OptionType[] =
					environmentOptions.length > 0 ? [environmentOptions[0]] : [];

				const environmentFilter = createSingleSelectEnvironmentFilter(
					sanitized.displayName || sanitized.label || 'Environment',
					defaultEnvironmentValue
				);

				allFilters.push(environmentFilter);
				allOptions[ENVIRONMENT_LABEL_KEY] = environmentOptions;
			}
		}
		allFilters.push(DATE_RANGE_FILTER);

		const labelsExcludedFromDashboard = buildLabelsExcludedFromDashboard(coreLabels);

		return {
			filterDefinitions: allFilters,
			filterOptions: allOptions,
			labelsExcludedFromDashboard
		};
	}, [configuration]);

	return { filterDefinitions, filterOptions, labelsExcludedFromDashboard };
};
